'use client'

import { isObjectPage } from '@/services/pathname'
import { ReactNode, useId, useEffect, useRef, useState } from 'react'
import { object, z } from 'zod'
import { messages } from '@/services/validation'
import { status as s } from '@/services/status'
import { executeRecaptcha, useRecaptcha } from '@/services/recaptcha/client'
import { postSharing } from '@/api/actions'
import Loading from '@/components/loading/Preloader'

type ShareProps = {
    aroeven: Aroeven,
    information?: string | ReactNode,
    type: string,
    object: any
}

const ShareSchema = z.object({
    firstName: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    lastName: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    email: z.string().trim().min(1, messages.required).email(messages.email)
})

export default function Share({
    aroeven,
    information,
    type,
    object
}: ShareProps) {

    const newShare = {
        firstName: '',
        lastName: '',
        email: '',
        url: '',
        targetType: type,
        targetId: object.id
    }


    const id = useId().replaceAll(':', '')
    const refModal = useRef(null)
    const [share, setShare] = useState({
        ...newShare
    })
    const [status, setStatus] = useState<string>(s.idle)
    const [errors, setErrors] = useState([])
    const [opened, setOpened] = useState<boolean>(false)

    useRecaptcha(opened)



    useEffect(() => {
        async function load() {
            const bootstrap = (await import('bootstrap/dist/js/bootstrap')).default;
            refModal.current = new bootstrap.Modal(document.getElementById(id + '-modal'))
            document.getElementById(id + '-modal').addEventListener('hidden.bs.modal', (e) => {
                setErrors([])
                setShare({
                    ...newShare
                })
                setStatus(s.idle)
                setOpened(false)
            })
        }
        load()
    }, [])

    async function handleSubmit() {
        setErrors([])
        try {
            ShareSchema.parse(share)
        }
        catch (error) {
            setErrors(error.issues)
            return
        }

        setStatus(s.loading)
        try {
            const token = await executeRecaptcha('share')
            await postSharing(aroeven, {
                ...share,
                url: window.location.href
            }, token)
            setStatus(s.finished)
        }
        catch (error) {
            console.log(error)
            setStatus(s.error)
        }
    }

    return (
        <>
            {status === s.loading &&
                <Loading />
            }
            <a className="button-outline share" data-bs-toggle="modal" data-bs-target={"#" + id + '-modal'} id={id + '-button'} onClick={() => setOpened(true)}>
                Partager <i className="fi fi-rr-share ms-1" />
            </a>
            <div className="modal fade" id={id + '-modal'} tabIndex={-1} role="dialog" aria-labelledby={id + '-button'} aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content border-0">
                        <div className="modal-header modal-colored-header bg-primary text-white">
                            <h4 className="modal-title text-white" id="primary-header-modalLabel">Partager cette page</h4>
                            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body">
                            <div className="card-body p-3">
                                {status === s.finished ?
                                    <div className="alert alert-success" role="alert">
                                        <h4 className="alert-heading">Bien joué !</h4>
                                        <p>Votre message a bien été envoyé.</p>
                                    </div>
                                    :
                                    <form onSubmit={(e) => {
                                        e.preventDefault()
                                        handleSubmit()
                                    }} id="primary-header-modal-creer">
                                        {status === s.error &&
                                            <div className="alert alert-danger" role="alert">
                                                <h4 className="alert-heading">Une erreur est survenue.</h4>
                                                <p>Vérifiez votre saisie ou réessayez plus tard.</p>
                                            </div>
                                        }
                                        <div className="row x-gap-20 y-gap-20">
                                            <h4>Saisissez vos informations</h4>
                                            <div className="col-md-6">
                                                <div className="form-input">
                                                    <input type="text" className={(errors.map(e => e.path[0]).indexOf('lastName') !== -1 ? 'requis' : '')} value={share.lastName} onChange={(e) => {
                                                        setShare({
                                                            ...share,
                                                            lastName: e.target.value
                                                        })
                                                    }} disabled={![s.idle, s.error].includes(status)} />
                                                    <label className="lh-1 text-light-1">
                                                        Votre nom <span className="obligatoire">*</span>
                                                    </label>
                                                </div>
                                                {errors.map(e => e.path[0]).indexOf('lastName') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'lastName')[0].message}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-input">
                                                    <input type="text" className={(errors.map(e => e.path[0]).indexOf('firstName') !== -1 ? 'requis' : '')} value={share.firstName} onChange={(e) => {
                                                        setShare({
                                                            ...share,
                                                            firstName: e.target.value
                                                        })
                                                    }} disabled={![s.idle, s.error].includes(status)} />
                                                    <label className="lh-1 text-light-1">
                                                        Votre prénom <span className="obligatoire">*</span>
                                                    </label>
                                                </div>
                                                {errors.map(e => e.path[0]).indexOf('firstName') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'lastName')[0].message}</div>}
                                            </div>
                                            <div className="col-12">
                                                <div className="form-input">
                                                    <input type="text" className={(errors.map(e => e.path[0]).indexOf('email') !== -1 ? 'requis' : '')} value={share.email} onChange={(e) => {
                                                        setShare({
                                                            ...share,
                                                            email: e.target.value
                                                        })
                                                    }} disabled={![s.idle, s.error].includes(status)} />
                                                    <label className="lh-1 text-light-1">Email du destinataire</label>
                                                </div>
                                                {errors.map(e => e.path[0]).indexOf('email') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'email')[0].message}</div>}
                                            </div>
                                            <p className="mb-0">{information ?? <>Un email sera transmis au destinataire contenant l'URL de cette page.</>}</p>
                                        </div>
                                    </form>
                                }
                            </div>
                        </div>
                        <div className="modal-footer pt-0 pe-4">
                            {status === s.finished ?
                                <>
                                    <button className="button-outline" data-bs-dismiss="modal">Fermer</button>
                                </>
                                :
                                <>
                                    <button className="button-outline me-1" data-bs-dismiss="modal">Annuler</button>
                                    <button className="button-primary" onClick={(e) => {
                                        e.preventDefault()
                                        handleSubmit()
                                    }}><i className="fi fi-rr-paper-plane me-1" />Envoyer</button>
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

